import React, { useState, useEffect, useRef } from 'react';
import { ChatProvider, useChat } from './contexts/ChatContext';
import Sidebar from './components/Sidebar';
import FileUpload from './components/FileUpload';
import CodeBlock from './components/CodeBlock';
import SettingsPanel from './components/SettingsPanel';
import { sendToAI } from './services/aiService';
import { exportToTXT, exportToJSON, exportToMarkdown, exportToHTML, exportToCSV, exportToDOCX, exportToPDF, downloadFile } from './services/exportService';
import './App.css';

function ChatInterface() {
  const { 
    getCurrentChat, 
    addMessage, 
    clearChat,
    currentChatId,
    renameChat 
  } = useChat();
  
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);

  const currentChat = getCurrentChat();
  const messages = currentChat?.messages || [];

  // Load theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const savedFontSize = localStorage.getItem('fontSize') || 'medium';
    document.body.className = savedTheme;
    document.body.setAttribute('data-font-size', savedFontSize);
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-rename
  useEffect(() => {
    if (currentChat && messages.length === 1 && currentChat.name === 'New Chat') {
      const firstMsg = messages[0].content.substring(0, 30);
      renameChat(currentChatId, firstMsg);
    }
  }, [messages, currentChat, currentChatId, renameChat]);

  const handleFilesSelected = (files) => {
    setUploadedFiles(files);
    setShowFileUpload(false);
  };

  const sendMessage = async () => {
    if ((!inputValue.trim() && uploadedFiles.length === 0) || isProcessing || !currentChatId) return;

    let messageContent = inputValue;

    if (uploadedFiles.length > 0) {
      const fileTexts = uploadedFiles.map(f => f.displayText || `[File: ${f.name}]`).join('\n\n');
      messageContent = inputValue ? `${inputValue}\n\n${fileTexts}` : fileTexts;
    }

    const userMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
      files: uploadedFiles.map(f => ({ name: f.name, type: f.type, note: f.note }))
    };

    addMessage(currentChatId, userMessage);
    setInputValue('');
    setUploadedFiles([]);
    setIsProcessing(true);

    try {
      const updatedMessages = [...messages, userMessage];
      const aiResponse = await sendToAI(updatedMessages);
      
      const aiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      
      addMessage(currentChatId, aiMessage);
    } catch (error) {
      console.error('Error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.message}`,
        timestamp: new Date().toISOString()
      };
      
      addMessage(currentChatId, errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClearChat = () => {
    if (currentChatId && window.confirm('Clear all messages in this chat?')) {
      clearChat(currentChatId);
    }
  };

  const handleExport = async (format) => {
    if (!currentChat) return;
    
    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    const baseName = `${currentChat.name}_${timestamp}`;
    
    let content, filename, mimeType;
    
    try {
      switch(format) {
        case 'txt':
          content = exportToTXT(currentChat);
          filename = `${baseName}.txt`;
          mimeType = 'text/plain';
          break;
        case 'json':
          content = exportToJSON(currentChat);
          filename = `${baseName}.json`;
          mimeType = 'application/json';
          break;
        case 'md':
          content = exportToMarkdown(currentChat);
          filename = `${baseName}.md`;
          mimeType = 'text/markdown';
          break;
        case 'html':
          content = exportToHTML(currentChat);
          filename = `${baseName}.html`;
          mimeType = 'text/html';
          break;
        case 'csv':
          content = exportToCSV(currentChat);
          filename = `${baseName}.csv`;
          mimeType = 'text/csv';
          break;
        case 'docx':
          content = await exportToDOCX(currentChat);
          filename = `${baseName}.docx`;
          mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          break;
        case 'pdf':
          content = exportToPDF(currentChat);
          filename = `${baseName}.pdf`;
          mimeType = 'application/pdf';
          break;
        default:
          return;
      }
      
      downloadFile(content, filename, mimeType);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed: ' + error.message);
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
  };

  const regenerateResponse = async (messageIndex) => {
    const userMessages = messages.slice(0, messageIndex).filter(m => m.role === 'user');
    if (userMessages.length === 0) return;

    setIsProcessing(true);

    try {
      const contextMessages = messages.slice(0, messageIndex);
      const aiResponse = await sendToAI(contextMessages);
      
      const updatedMessages = [...messages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      
      clearChat(currentChatId);
      updatedMessages.forEach(msg => addMessage(currentChatId, msg));
      
    } catch (error) {
      console.error('Regeneration error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteMessage = (index) => {
    if (window.confirm('Delete this message?')) {
      const updatedMessages = messages.filter((_, i) => i !== index);
      clearChat(currentChatId);
      updatedMessages.forEach(msg => addMessage(currentChatId, msg));
    }
  };

  const renderMessageContent = (content) => {
    const codeBlockRegex = /```(\w+)?\n([\s\S]+?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }
      
      parts.push({
        type: 'code',
        language: match[1] || 'text',
        content: match[2].trim()
      });
      
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    if (parts.length === 0) {
      return <span>{content}</span>;
    }

    return parts.map((part, idx) => 
      part.type === 'code' ? (
        <CodeBlock key={idx} code={part.content} language={part.language} />
      ) : (
        <span key={idx}>{part.content}</span>
      )
    );
  };

  return (
    <div className="App">
      <div className={`app-container ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        {sidebarOpen && <Sidebar />}
        
        <div className="main-content">
          <div className="header">
            <button 
              className="toggle-sidebar-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
            
            <h1>{currentChat?.name || 'Baba AI'}</h1>
            
            <div className="header-actions">
              <button 
                className="header-btn"
                onClick={() => setShowFileUpload(!showFileUpload)}
                title="Upload files"
              >
                📎
              </button>
              
              <div className="export-menu-container">
                <button 
                  className="header-btn"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  title="Export chat"
                >
                  ⬇️
                </button>
                {showExportMenu && (
                  <div className="export-menu">
                    <button onClick={() => handleExport('txt')}>📄 Export TXT</button>
                    <button onClick={() => handleExport('json')}>📊 Export JSON</button>
                    <button onClick={() => handleExport('md')}>📝 Export Markdown</button>
                    <button onClick={() => handleExport('html')}>🌐 Export HTML</button>
                    <button onClick={() => handleExport('csv')}>📈 Export CSV</button>
                    <button onClick={() => handleExport('docx')}>📝 Export DOCX</button>
                    <button onClick={() => handleExport('pdf')}>📕 Export PDF</button>
                  </div>
                )}
              </div>
              
              <button 
                className="header-btn"
                onClick={() => setShowSettings(true)}
                title="Settings"
              >
                ⚙️
              </button>
              
              <button className="header-btn clear-btn" onClick={handleClearChat}>
                Clear
              </button>
            </div>
          </div>

          {showFileUpload && (
            <div className="file-upload-panel">
              <FileUpload 
                onFilesSelected={handleFilesSelected}
                disabled={isProcessing}
              />
            </div>
          )}

          <div className="messages-container">
            {messages.length === 0 && (
              <div className="welcome-message">
                <h2>🖤 Baba AI</h2>
                <p>Type a message or upload files</p>
                <p className="welcome-hint">Supports: images, text, JSON, PDF, Excel, Word</p>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className="message-content">
                  {renderMessageContent(msg.content)}
                </div>
                {msg.files && msg.files.length > 0 && (
                  <div className="message-files">
                    {msg.files.map((f, i) => (
                      <span key={i} className="file-badge" title={f.note}>
                        {f.name}
                        {f.note && <span className="info-icon">ℹ️</span>}
                      </span>
                    ))}
                  </div>
                )}
                <div className="message-actions">
                  <button 
                    className="msg-action-btn"
                    onClick={() => copyMessage(msg.content)}
                    title="Copy"
                  >
                    📋
                  </button>
                  {msg.role === 'assistant' && (
                    <button 
                      className="msg-action-btn"
                      onClick={() => regenerateResponse(idx)}
                      title="Regenerate"
                      disabled={isProcessing}
                    >
                      🔄
                    </button>
                  )}
                  <button 
                    className="msg-action-btn"
                    onClick={() => deleteMessage(idx)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
            
            {isProcessing && (
              <div className="message assistant">
                <div className="message-content typing">
                  Thinking...
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {uploadedFiles.length > 0 && (
            <div className="uploaded-files-preview">
              {uploadedFiles.map((file, idx) => (
                <div key={idx} className="file-chip" title={file.note}>
                  {file.isImage && (
                    <img src={file.preview} alt={file.name} className="file-chip-img" />
                  )}
                  <span className="file-chip-name">{file.name}</span>
                  {file.note && <span className="info-icon">ℹ️</span>}
                  <button 
                    className="file-chip-remove"
                    onClick={() => setUploadedFiles(uploadedFiles.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="input-container">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Shift+Enter for new line)"
              rows="3"
              disabled={isProcessing}
            />
            <button 
              onClick={sendMessage} 
              disabled={isProcessing || (!inputValue.trim() && uploadedFiles.length === 0)}
            >
              {isProcessing ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      {showSettings && (
        <SettingsPanel onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

function App() {
  return (
    <ChatProvider>
      <ChatInterface />
    </ChatProvider>
  );
}

export default App;
